import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ticketApi } from './api';
import type { Ticket, TicketStats } from './api';
import StatusBadge from './StatusBadge';
import SlaPill from './SlaPill';

export default function OpsDashboard() {
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function loadDashboard() {
    try {
      const [summary, list] = await Promise.all([ticketApi.getStats(), ticketApi.listTickets()]);
      setStats(summary);
      setTickets(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load dashboard');
    }
  }

  useEffect(() => {
    loadDashboard();
  }, []);

  if (error) return <div className="ticket-alert error">{error}</div>;
  if (!stats) return <p className="ticket-muted">Loading dashboard...</p>;

  const active = tickets
    .filter((ticket) => ticket.sla.isActive)
    .sort((a, b) => a.sla.hoursToResolutionDue - b.sla.hoursToResolutionDue);

  return (
    <div className="ticket-dashboard">
      <h2>Operations dashboard</h2>

      <div className="ticket-cards-row">
        <Stat label="Total tickets" value={stats.total} />
        <Stat label="Open" value={stats.open} />
        <Stat label="Resolved" value={stats.resolved} />
        <Stat label="SLA at risk" value={stats.slaAtRisk} tone={stats.slaAtRisk ? 'warn' : ''} />
        <Stat label="SLA breached" value={stats.slaBreached} tone={stats.slaBreached ? 'danger' : ''} />
      </div>

      <div className="ticket-dash-grid">
        <div className="ticket-card">
          <h3>By status</h3>
          <BreakdownBars data={stats.byStatus} />
        </div>
        <div className="ticket-card">
          <h3>By category</h3>
          <BreakdownBars data={stats.byCategory} />
        </div>
      </div>

      <div className="ticket-card">
        <h3>Active tickets - SLA watch</h3>
        <div className="ticket-table-wrap">
          <table className="ticket-table">
            <thead>
              <tr><th>Reference</th><th>Subject</th><th>Category</th><th>Priority</th><th>Status</th><th>Assigned</th><th>SLA</th></tr>
            </thead>
            <tbody>
              {active.length === 0 && <tr><td colSpan={7} className="ticket-muted center">No active tickets.</td></tr>}
              {active.map((ticket) => (
                <tr key={ticket.id} className={ticket.sla.resolutionState === 'breached' ? 'row-danger' : ticket.sla.resolutionState === 'at_risk' ? 'row-warn' : ''}>
                  <td><Link to={`/tickets/view/${ticket.id}`}>{ticket.reference}</Link></td>
                  <td>{ticket.subject}</td>
                  <td>{ticket.category}</td>
                  <td>{ticket.priority}</td>
                  <td><StatusBadge status={ticket.status} /></td>
                  <td className="ticket-muted">{ticket.assignedTo || '-'}</td>
                  <td><SlaPill sla={ticket.sla} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className={`ticket-stat ${tone || ''}`}>
      <div className="ticket-stat-value">{value}</div>
      <div className="ticket-stat-label">{label}</div>
    </div>
  );
}

function BreakdownBars({ data }: { data: Record<string, number> }) {
  const entries = Object.entries(data).filter(([, value]) => value > 0);
  const max = Math.max(1, ...entries.map(([, value]) => value));
  if (!entries.length) return <p className="ticket-muted">No data.</p>;

  return (
    <div className="ticket-bars">
      {entries.map(([key, value]) => (
        <div key={key} className="ticket-bar-row">
          <span className="ticket-bar-label">{key.replace('_', ' ')}</span>
          <span className="ticket-bar-track"><span className="ticket-bar-fill" style={{ width: `${(value / max) * 100}%` }} /></span>
          <span className="ticket-bar-val">{value}</span>
        </div>
      ))}
    </div>
  );
}
